import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Star, Search, Sparkles, Users, Award, Loader2, X, Save,
  CheckCircle2, XCircle, AlertCircle, TrendingUp, ChevronLeft, ExternalLink, Globe,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

const REC_META = {
  award:  { label: "À labelliser", color: "#15803D", bg: "rgba(21,128,61,0.10)", Icon: CheckCircle2 },
  watch:  { label: "À surveiller", color: "#B45309", bg: "rgba(180,83,9,0.10)", Icon: AlertCircle },
  reject: { label: "À décliner",   color: "#B91C1C", bg: "rgba(185,28,28,0.10)", Icon: XCircle },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function AdminEliteTalents() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({ max_slots: 100, current_count: 0, available: 100, showcase_enabled: false });
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [onlyElite, setOnlyElite] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // detail modal
  const [evaluations, setEvaluations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [maxSlotsInput, setMaxSlotsInput] = useState("100");
  const [togglingShowcase, setTogglingShowcase] = useState(false);

  const isAdmin = ["admin", "super-admin"].includes(user?.role);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st, c] = await Promise.all([
        api.get("/v1/admin/elite/settings"),
        api.get("/v1/admin/elite/stats"),
        api.get("/v1/admin/elite/candidates", { params: { q, role: role || undefined, only_elite: onlyElite } }),
      ]);
      setSettings(s.data);
      setMaxSlotsInput(String(s.data.max_slots));
      setStats(st.data);
      setCandidates(c.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, role, onlyElite]);

  useEffect(() => { if (isAdmin) loadAll(); }, [isAdmin, loadAll]);

  const openDetail = async (u) => {
    setActive(u);
    setEvaluations([]);
    try {
      const { data } = await api.get(`/v1/admin/elite/evaluations/${u.id}`);
      setEvaluations(data.items || []);
    } catch { /* silent */ }
  };

  const runEvaluation = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/v1/admin/elite/evaluate/${active.id}`);
      toast.success(`Évaluation terminée : score ${data.evaluation.score}/100`);
      setEvaluations((arr) => [data.evaluation, ...arr]);
      await loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const awardLabel = async () => {
    if (!active) return;
    if (!window.confirm(`Attribuer le label ⭐ AFRICTAL Elite Talent à ${active.name} ?`)) return;
    setBusy(true);
    try {
      await api.post(`/v1/admin/elite/label/${active.id}`, { notes: "" });
      toast.success(`⭐ Label attribué à ${active.name}`);
      setActive({ ...active, is_elite: true });
      await loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const revokeLabel = async () => {
    if (!active) return;
    if (!window.confirm(`Retirer le label à ${active.name} ?`)) return;
    setBusy(true);
    try {
      await api.delete(`/v1/admin/elite/label/${active.id}`);
      toast.success(`Label retiré à ${active.name}`);
      setActive({ ...active, is_elite: false });
      await loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const saveSettings = async () => {
    try {
      const n = parseInt(maxSlotsInput, 10);
      if (!Number.isFinite(n) || n < 1) { toast.error("Limite invalide"); return; }
      const { data } = await api.patch("/v1/admin/elite/settings", { max_slots: n });
      setSettings(data);
      toast.success("Limite mise à jour");
      setShowSettings(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleShowcase = async () => {
    setTogglingShowcase(true);
    try {
      const next = !settings.showcase_enabled;
      const { data } = await api.patch("/v1/admin/elite/settings", { showcase_enabled: next });
      setSettings(data);
      toast.success(next ? "Vitrine AFRICTAL Elite activée · route /elite en ligne" : "Vitrine désactivée · la page affiche un message professionnel");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTogglingShowcase(false); }
  };

  if (!isAdmin) {
    return (
      <PrivateLayout title="AFRICTAL Elite Talent" breadcrumbs={[{ label: "Elite Talent" }]}>
        <div className="afr-empty" data-testid="elite-forbidden">
          <div className="afr-empty__title">Accès restreint</div>
          <div className="afr-empty__lead">Seul le Super Administrateur peut accéder à ce module.</div>
        </div>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout
      title="⭐ AFRICTAL Elite Talent"
      breadcrumbs={[{ to: "/admin", label: "Admin" }, { label: "Elite Talent" }]}
      actions={
        <Link to="/admin" className="afr-btn afr-btn--outline afr-btn--sm"><ChevronLeft size={14} /> Admin</Link>
      }
    >
      {/* Vitrine publique — bandeau de contrôle */}
      <div
        className="afr-card"
        data-testid="showcase-toggle-panel"
        style={{
          padding: 16, marginBottom: 20,
          borderLeft: `4px solid ${settings.showcase_enabled ? "#15803D" : "#B45309"}`,
          background: settings.showcase_enabled ? "rgba(21,128,61,0.05)" : "rgba(180,83,9,0.05)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <Globe size={16} color={settings.showcase_enabled ? "#15803D" : "#B45309"} />
              <strong style={{ color: "var(--afr-secondary)" }}>Vitrine publique AFRICTAL Elite</strong>
              <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: settings.showcase_enabled ? "#15803D" : "#B45309", color: "#fff" }} data-testid="showcase-status-badge">
                {settings.showcase_enabled ? "ACTIVÉE" : "DÉSACTIVÉE"}
              </span>
            </div>
            <div className="afr-muted" style={{ fontSize: "0.86rem" }}>
              {settings.showcase_enabled
                ? "La page /elite est publique. Seuls les membres Elite qui l'ont explicitement autorisée y apparaissent."
                : "La page /elite reste accessible mais affiche un message professionnel « bientôt disponible »."}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Link to="/elite" target="_blank" rel="noopener noreferrer" className="afr-btn afr-btn--outline afr-btn--sm" data-testid="showcase-open-link">
              <ExternalLink size={12} /> Ouvrir /elite
            </Link>
            <button
              type="button"
              className={`afr-btn afr-btn--sm ${settings.showcase_enabled ? "afr-btn--outline" : "afr-btn--primary"}`}
              onClick={toggleShowcase}
              disabled={togglingShowcase}
              data-testid="showcase-toggle-btn"
            >
              {togglingShowcase ? <Loader2 size={12} className="afr-spin" /> : null}
              {settings.showcase_enabled ? "Désactiver la vitrine" : "Activer la vitrine"}
            </button>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }} data-testid="elite-kpis">
        <KpiCard Icon={Users} label="Membres actifs" value={`${settings.current_count} / ${settings.max_slots}`} accent="#009640" testid="kpi-active" />
        <KpiCard Icon={Star} label="Places disponibles" value={settings.available} accent="#0F2A42" testid="kpi-available" />
        <KpiCard Icon={TrendingUp} label="Évaluations IA" value={stats?.total_evaluations ?? "—"} accent="#B45309" testid="kpi-evals" />
        <KpiCard Icon={Sparkles} label="Score moyen" value={stats?.avg_score ?? "—"} accent="#15803D" testid="kpi-avg" />
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <label className="afr-search" style={{ flex: "1 1 300px" }}>
          <Search size={16} />
          <input
            placeholder="Rechercher un talent ou freelance…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadAll()}
            data-testid="elite-search-input"
          />
        </label>
        <select className="afr-input" value={role} onChange={(e) => setRole(e.target.value)} data-testid="elite-role-filter" style={{ maxWidth: 200 }}>
          <option value="">Tous rôles</option>
          <option value="talent">Talents</option>
          <option value="freelance">Freelances</option>
        </select>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: "0.86rem" }}>
          <input type="checkbox" checked={onlyElite} onChange={(e) => setOnlyElite(e.target.checked)} data-testid="elite-only-toggle" />
          Uniquement Elite
        </label>
        <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={loadAll} data-testid="elite-search-btn">Rechercher</button>
        <button className="afr-btn afr-btn--ghost afr-btn--sm" onClick={() => setShowSettings(true)} data-testid="elite-settings-btn">
          Configurer la limite ({settings.max_slots})
        </button>
      </div>

      {loading ? (
        <div data-testid="elite-loading">Chargement…</div>
      ) : (
        <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }} data-testid="elite-candidates-list">
          {candidates.map((c) => (
            <article
              key={c.id}
              className="afr-card afr-clickable"
              data-testid={`elite-candidate-${c.id}`}
              onClick={() => openDetail(c)}
              style={{ cursor: "pointer", borderLeft: c.is_elite ? "4px solid #F59E0B" : "4px solid transparent" }}
            >
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                {c.avatar_url ? (
                  <img src={c.avatar_url} alt={c.name} style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", background: "#eee" }} />
                ) : (
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg,#009640,#0F2A42)", color: "#fff", display: "grid", placeItems: "center", fontWeight: 700 }}>
                    {(c.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <strong>{c.name}</strong>
                    {c.is_elite && (
                      <span data-testid={`elite-badge-${c.id}`} title="AFRICTAL Elite Talent" style={{
                        display: "inline-flex", alignItems: "center", gap: 2, padding: "2px 6px",
                        borderRadius: 999, background: "linear-gradient(135deg,#F59E0B,#B45309)",
                        color: "#fff", fontSize: 10, fontWeight: 700,
                      }}>
                        <Star size={10} /> ELITE
                      </span>
                    )}
                  </div>
                  <div className="afr-muted" style={{ fontSize: "0.82rem" }}>
                    {c.role}{c.headline ? ` · ${c.headline}` : ""}
                  </div>
                </div>
              </div>
              {c.last_evaluation && (
                <div style={{ marginTop: 10, fontSize: "0.78rem", color: "#475569" }}>
                  Dernière éval : <strong>{c.last_evaluation.score}/100</strong> · {c.last_evaluation.recommendation}
                </div>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
                {(c.skills || []).slice(0, 4).map((s) => <span key={s} className="afr-tag-chip">{s}</span>)}
              </div>
            </article>
          ))}
          {candidates.length === 0 && (
            <div className="afr-empty" style={{ gridColumn: "1 / -1" }} data-testid="elite-empty">
              <div className="afr-empty__title">Aucun candidat trouvé</div>
            </div>
          )}
        </div>
      )}

      {active && (
        <div role="dialog" data-testid="elite-detail-modal" onClick={() => setActive(null)} style={{ position: "fixed", inset: 0, background: "rgba(15,42,66,0.5)", display: "grid", placeItems: "center", zIndex: 80, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, maxWidth: 720, width: "100%", padding: 20, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(135deg,#009640,#0F2A42)", color: "#fff", display: "grid", placeItems: "center", fontWeight: 700 }}>
                  {(active.name || "?").charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <strong style={{ fontSize: "1.05rem" }}>{active.name}</strong>
                    {active.is_elite && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 2, padding: "2px 8px", borderRadius: 999, background: "linear-gradient(135deg,#F59E0B,#B45309)", color: "#fff", fontSize: 11, fontWeight: 700 }}>
                        <Star size={12} /> ELITE
                      </span>
                    )}
                  </div>
                  <div className="afr-muted" style={{ fontSize: "0.86rem" }}>{active.role} · {active.headline || "—"}</div>
                </div>
              </div>
              <button type="button" onClick={() => setActive(null)} style={{ border: 0, background: "transparent", cursor: "pointer" }} aria-label="Fermer"><X size={16} /></button>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              <button
                type="button"
                className="afr-btn afr-btn--primary afr-btn--sm"
                onClick={runEvaluation}
                disabled={busy}
                data-testid="elite-run-eval"
              >
                {busy ? <Loader2 size={14} className="afr-spin" /> : <Sparkles size={14} />} Évaluer avec l'IA
              </button>
              {active.is_elite ? (
                <button type="button" className="afr-btn afr-btn--outline afr-btn--sm" onClick={revokeLabel} disabled={busy} data-testid="elite-revoke" style={{ color: "#B91C1C" }}>
                  <X size={14} /> Retirer le label
                </button>
              ) : (
                <button type="button" className="afr-btn afr-btn--secondary afr-btn--sm" onClick={awardLabel} disabled={busy} data-testid="elite-award">
                  <Award size={14} /> Attribuer le label ⭐
                </button>
              )}
            </div>

            <h4 style={{ marginTop: 20, marginBottom: 8, color: "var(--afr-secondary)" }}>Évaluations IA</h4>
            {evaluations.length === 0 ? (
              <div className="afr-muted" style={{ fontSize: "0.9rem" }}>Aucune évaluation. Lancez une évaluation IA pour obtenir une recommandation.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="elite-evaluations-list">
                {evaluations.map((e) => {
                  const meta = REC_META[e.recommendation] || REC_META.watch;
                  const RecIcon = meta.Icon;
                  return (
                    <article key={e.id} className="afr-card" data-testid={`elite-eval-${e.id}`} style={{ padding: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <strong>Score : {e.score}/100</strong>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 999, background: meta.bg, color: meta.color, fontSize: 11, fontWeight: 700 }}>
                              <RecIcon size={12} /> {meta.label}
                            </span>
                          </div>
                          <div className="afr-muted" style={{ fontSize: "0.78rem" }}>Le {fmt(e.created_at)} · par {e.requested_by_name || "—"} · IA : {e.ai_provider}</div>
                        </div>
                      </div>
                      {(e.strengths || []).length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ fontSize: "0.78rem", color: "#15803D", fontWeight: 700 }}>Points forts</div>
                          <ul style={{ paddingLeft: 18, margin: "4px 0", fontSize: "0.86rem" }}>
                            {e.strengths.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                      )}
                      {(e.areas_to_improve || []).length > 0 && (
                        <div>
                          <div style={{ fontSize: "0.78rem", color: "#B45309", fontWeight: 700 }}>Axes d'amélioration</div>
                          <ul style={{ paddingLeft: 18, margin: "4px 0", fontSize: "0.86rem" }}>
                            {e.areas_to_improve.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                      )}
                      {e.report && (
                        <div style={{ marginTop: 8, padding: 10, background: "rgba(15,42,66,0.04)", borderRadius: 8, fontSize: "0.85rem", whiteSpace: "pre-wrap" }}>
                          {e.report}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {showSettings && (
        <div role="dialog" data-testid="elite-settings-modal" onClick={() => setShowSettings(false)} style={{ position: "fixed", inset: 0, background: "rgba(15,42,66,0.5)", display: "grid", placeItems: "center", zIndex: 80, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, maxWidth: 400, width: "100%", padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <strong>Configurer la limite</strong>
              <button type="button" onClick={() => setShowSettings(false)} style={{ border: 0, background: "transparent", cursor: "pointer" }}><X size={16} /></button>
            </div>
            <label style={{ display: "block", fontSize: 12, color: "#64748b", marginBottom: 6 }}>Nombre maximum de membres AFRICTAL Elite Talent</label>
            <input type="number" className="afr-input" value={maxSlotsInput} onChange={(e) => setMaxSlotsInput(e.target.value)} data-testid="elite-max-slots-input" min="1" />
            <div className="afr-muted" style={{ fontSize: "0.78rem", marginTop: 6 }}>Actuellement {settings.current_count} membre(s) actif(s). La limite ne peut pas être inférieure aux membres actifs.</div>
            <button type="button" className="afr-btn afr-btn--primary" onClick={saveSettings} style={{ marginTop: 16 }} data-testid="elite-save-settings">
              <Save size={14} /> Enregistrer
            </button>
          </div>
        </div>
      )}
    </PrivateLayout>
  );
}

function KpiCard({ Icon, label, value, accent, testid }) {
  return (
    <div className="afr-card" style={{ padding: 14, borderLeft: `3px solid ${accent}` }} data-testid={testid}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <Icon size={16} color={accent} />
        <span className="afr-muted" style={{ fontSize: "0.8rem" }}>{label}</span>
      </div>
      <div style={{ fontSize: "1.4rem", fontWeight: 700, color: "var(--afr-secondary)" }}>{value}</div>
    </div>
  );
}
